import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  BookOpen, 
  Trophy, 
  Headphones, 
  Users,
  Zap,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  TrendingUp,
  Activity
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hook personnalisé pour animer les nombres (compteur qui monte)
const useCountUp = (end, duration = 1000) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      
      // Easing function pour un effet plus naturel (ease-out cubic)
      const easeOut = 1 - Math.pow(1 - percentage, 3);
      setCount(Math.floor(end * easeOut));

      if (percentage < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return count;
};

// Mini graphique sparkline (petit graphique de tendance)
const Sparkline = ({ data, color }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 60;
    const y = 20 - ((value - min) / range) * 20;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width="60" height="20" className="opacity-50">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        points={points}
      />
    </svg>
  );
};

// Composant pour afficher une stat avec animation
const AnimatedStat = ({ 
  icon: Icon, 
  label, 
  value, 
  previousValue, 
  color,
  history = [] 
}) => {
  const animatedValue = useCountUp(value, 1200);
  const [showChange, setShowChange] = useState(false);
  const change = value - (previousValue || value);
  const changePercent = previousValue ? ((change / previousValue) * 100).toFixed(0) : 0;

  useEffect(() => {
    if (change !== 0) {
      setShowChange(true);
      const timer = setTimeout(() => setShowChange(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [change]);

  return (
    <div className="relative group">
      <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-br from-muted/50 to-muted/30 transition-all hover:from-muted/70 hover:to-muted/50 hover:scale-[1.02] hover:shadow-md relative overflow-hidden">
        {/* Effet de pulse si nouvelle valeur */}
        {showChange && change > 0 && (
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/20 to-transparent animate-shimmer pointer-events-none" />
        )}
        
        <div className="flex items-center gap-3 relative z-10">
          <div className={`p-2 rounded-lg bg-background/50 transition-transform group-hover:scale-110`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
          <div>
            <span className="text-sm font-medium text-muted-foreground block">{label}</span>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`font-bold text-2xl ${color}`}>
                {animatedValue.toLocaleString()}
              </span>
              {/* Indicateur de changement */}
              {showChange && change !== 0 && (
                <span className={`flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full ${
                  change > 0 
                    ? 'bg-green-500/20 text-green-600 dark:text-green-400' 
                    : 'bg-red-500/20 text-red-600 dark:text-red-400'
                } animate-bounce`}>
                  {change > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  {Math.abs(change)}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-1 relative z-10">
          {/* Mini graphique */}
          {history.length > 0 && (
            <Sparkline data={history} color={color.replace('text-', '')} />
          )}
          {/* Pourcentage de changement */}
          {previousValue && change !== 0 && (
            <span className={`text-xs font-semibold ${
              change > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            }`}>
              {change > 0 ? '+' : ''}{changePercent}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default function LiveActivity() {
  const [activityData, setActivityData] = useState(null);
  const [previousData, setPreviousData] = useState(null);
  const [history, setHistory] = useState({
    words: [],
    quizzes: [],
    audio: [],
    learners: []
  });
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchActivity = useCallback(async () => {
    try {
      setIsRefreshing(true);
      setError(null);
      
      // Essayer l'endpoint dédié
      const response = await fetch(`${API}/stats/live-activity`);
      
      if (response.ok) {
        const data = await response.json();
        updateActivityData(data);
      } else {
        // Fallback: utiliser l'endpoint general stats ou popular-content
        const fallbackResponse = await fetch(`${API}/trends/popular-content`);
        if (fallbackResponse.ok) {
          const popData = await fallbackResponse.json();
          const fallbackData = {
            words_learned_today: popData.recent_activity?.words_learned_today || 0,
            quizzes_completed_today: popData.recent_activity?.quizzes_completed_today || 0,
            audio_played_today: popData.recent_activity?.audio_played_today || 0,
            active_learners: popData.recent_activity?.active_learners || 0
          };
          updateActivityData(fallbackData);
        } else {
          throw new Error('Unable to fetch activity data');
        }
      }
    } catch (error) {
      console.error("Error fetching live activity:", error);
      setError("Impossible de charger les données");
      
      // Données de démo si l'API échoue (seulement au premier chargement)
      if (!activityData) {
        setActivityData({
          words_learned_today: 180,
          quizzes_completed_today: 45,
          audio_played_today: 374,
          active_learners: 34
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [activityData]);

  const updateActivityData = (data) => {
    setPreviousData(activityData);
    setActivityData(data);
    setLastUpdate(new Date());
    
    // Mettre à jour l'historique (garder les 10 dernières valeurs)
    setHistory(prev => ({
      words: [...prev.words, data.words_learned_today || 0].slice(-10),
      quizzes: [...prev.quizzes, data.quizzes_completed_today || 0].slice(-10),
      audio: [...prev.audio, data.audio_played_today || 0].slice(-10),
      learners: [...prev.learners, data.active_learners || 0].slice(-10)
    }));
  };

  // Fetch initial
  useEffect(() => {
    fetchActivity();
  }, []);

  // Auto-refresh toutes les 20 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchActivity();
    }, 20000); // 20 secondes

    return () => clearInterval(interval);
  }, [fetchActivity]);

  // Calculer le temps écoulé depuis la dernière mise à jour
  const [timeSinceUpdate, setTimeSinceUpdate] = useState('');
  
  useEffect(() => {
    const updateTimer = setInterval(() => {
      const seconds = Math.floor((new Date() - lastUpdate) / 1000);
      if (seconds < 60) {
        setTimeSinceUpdate(`il y a ${seconds}s`);
      } else {
        const minutes = Math.floor(seconds / 60);
        setTimeSinceUpdate(`il y a ${minutes}min`);
      }
    }, 1000);

    return () => clearInterval(updateTimer);
  }, [lastUpdate]);

  // Skeleton loader pendant le chargement
  if (!activityData) {
    return (
      <Card className="animate-slide-up stagger-1 border-2 border-accent/20">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Zap className="w-5 h-5 text-accent animate-pulse" />
            Activité en Direct
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-20 bg-muted/50 rounded-xl animate-pulse" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="animate-slide-up stagger-1 border-2 border-accent/20 shadow-lg">
      <CardHeader className="pb-3 bg-gradient-to-br from-accent/5 to-transparent">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <div className="relative">
              <Zap className="w-5 h-5 text-accent animate-pulse" />
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-ping" />
            </div>
            Activité en Direct
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/20 text-green-600 dark:text-green-400 text-xs font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              LIVE
            </span>
          </CardTitle>
          
          <button
            onClick={fetchActivity}
            disabled={isRefreshing}
            className="p-2 rounded-lg hover:bg-muted transition-all disabled:opacity-50 group"
            title="Actualiser"
          >
            <RefreshCw className={`w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors ${
              isRefreshing ? 'animate-spin' : ''
            }`} />
          </button>
        </div>
        
        <div className="flex items-center justify-between text-xs mt-2">
          <span className="text-muted-foreground flex items-center gap-1">
            <Activity className="w-3 h-3" />
            {timeSinceUpdate}
          </span>
          {error && (
            <span className="text-red-500">⚠️ {error}</span>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3 pt-4">
        <AnimatedStat
          icon={BookOpen}
          label="Mots appris aujourd'hui"
          value={activityData.words_learned_today || 0}
          previousValue={previousData?.words_learned_today}
          color="text-primary"
          history={history.words}
        />
        
        <AnimatedStat
          icon={Trophy}
          label="Quiz complétés"
          value={activityData.quizzes_completed_today || 0}
          previousValue={previousData?.quizzes_completed_today}
          color="text-accent"
          history={history.quizzes}
        />
        
        <AnimatedStat
          icon={Headphones}
          label="Audio joués"
          value={activityData.audio_played_today || 0}
          previousValue={previousData?.audio_played_today}
          color="text-secondary"
          history={history.audio}
        />
        
        <AnimatedStat
          icon={Users}
          label="Apprenants actifs"
          value={activityData.active_learners || 0}
          previousValue={previousData?.active_learners}
          color="text-green-500"
          history={history.learners}
        />
      </CardContent>
      
      {/* Footer avec info d'actualisation */}
      <div className="px-6 pb-4">
        <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-gradient-to-r from-muted/30 to-muted/10 text-xs">
          <span className="text-muted-foreground flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            Mise à jour auto
          </span>
          <span className="font-semibold text-accent">toutes les 20s</span>
        </div>
      </div>
    </Card>
  );
}
